import { SearchIcon, UserIcon, XIcon } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash.debounce';

interface SearchBarProps {
  onSearch: (query: string) => void;
  onUserSearch?: (username: string) => void;
  placeholder?: string;
  className?: string;
}

export default function SearchBar({ onSearch, onUserSearch, placeholder = 'Search...', className = '' }: SearchBarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isUserSearch, setIsUserSearch] = useState(false);

  // Create memoized debounced search handler
  const debouncedSearch = useMemo(
    () => debounce((query: string, isUser: boolean) => {
      if (isUser && onUserSearch) {
        onUserSearch(query);
      } else {
        onSearch(query);
      }
    }, 300),
    [onSearch, onUserSearch]
  );

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // If it starts with @, it's a user search
    const isUser = query.startsWith('@');
    setIsUserSearch(isUser);

    // Remove @ for user search
    const searchTerm = isUser ? query.substring(1) : query;
    
    // If search is empty, reset immediately without debounce
    if (!searchTerm.trim()) {
      debouncedSearch.cancel();
      if (isUser && onUserSearch) {
        onUserSearch('');
      } else {
        onSearch('');
      }
    } else {
      debouncedSearch(searchTerm, isUser);
    }
  };

  // Handle reset button click
  const handleReset = () => {
    setSearchQuery('');
    setIsUserSearch(false);
    debouncedSearch.cancel();
    if (onUserSearch) {
      onUserSearch('');
    }
    onSearch('');
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className={`relative flex-1 ${className}`}>
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        {isUserSearch ? (
          <UserIcon className="h-5 w-5" />
        ) : (
          <SearchIcon className="h-5 w-5" />
        )}
      </div>
      <input
        type="text"
        placeholder={isUserSearch ? 'Search by username...' : placeholder}
        value={searchQuery}
        onChange={handleSearchChange}
        className="w-full pl-10 pr-10 py-2 border-2 border-gray-200 rounded-lg focus:border-primary-500 focus:ring-primary-500"
      />
      {searchQuery && (
        <button
          onClick={handleReset}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          title="Clear search"
        >
          <XIcon className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}